export type AccessLevel = 'viewer' | 'editor';

export const GOVERNANCE_LICENSE_TYPE = 6;

export const GOVERNANCE_LICENSES = [
  { id: 0, name: 'issues' },
  { id: 1, name: 'customPolicies' },
] as const;

export const GOVERNANCE_LICENSE_IDS = Object.fromEntries(
  GOVERNANCE_LICENSES.map(({ id, name }) => [name, id]),
) as Record<(typeof GOVERNANCE_LICENSES)[number]['name'], number>;

export type TeamProductType =
  | 'assist'
  | 'audit'
  | 'report'
  | 'issuesContent'
  | 'moduleAccesses'
  | 'governance';

export interface TeamData {
  color: string | null;
  createdAt: string;
  createdByUserId: number | null;
  description: string | null;
  id: number;
  name: string;
  organisationId: number;
  updatedAt: string;
}

export interface TeamListData extends TeamData {
  licenseCount: number;
  memberCount: number;
}

export interface TeamDetailsData {
  members?: TeamMemberData[];
  rights?: TeamRightsData;
  team: TeamListData;
}

export interface TeamMemberUserData {
  email: string | null;
  firstName: string | null;
  fullName: string | null;
  isActive: number | boolean | null;
  isGuest?: 0 | 1;
  lastName: string | null;
  orgAdmin: number | boolean | null;
  primaryOrganisation: number | null;
  secondaryOrganisation: number | null;
}

export interface TeamMemberData {
  addedByUserId: number | null;
  createdAt: string;
  teamId: number;
  userId: number;
  user: TeamMemberUserData | null;
}

export interface TeamAdministrationRight {
  licenseId: number;
  licenseType: number;
  accessLevel: AccessLevel;
}

export interface TeamRightsData {
  licenseData: TeamAdministrationRight[];
  teamId: number;
}

export interface TeamEntitlementCatalogItem {
  label: string;
  licenseId: number;
  licenseType: number;
  productType: TeamProductType;
}

export interface OrganisationEntitlementCatalogData {
  items: TeamEntitlementCatalogItem[];
  organisationId: number;
}

export type TeamEffectiveRightSource =
  | { kind: 'directConstraint'; accessLevel: AccessLevel }
  | { kind: 'legacyDirectFullAccess'; accessLevel: AccessLevel }
  | {
      kind: 'team';
      teamColor: string | null;
      teamId: number;
      teamName: string;
      accessLevel: AccessLevel;
    };

export interface TeamEffectiveRight {
  licenseId: number;
  accessLevel: AccessLevel;
  sources: TeamEffectiveRightSource[];
}

export interface TeamEffectiveRightsByProductType {
  assist: TeamEffectiveRight[];
  audit: TeamEffectiveRight[];
  governance: TeamEffectiveRight[];
  issuesContent: TeamEffectiveRight[];
  moduleAccesses: TeamEffectiveRight[];
  report: TeamEffectiveRight[];
}

export interface TeamEffectiveMemberData extends TeamMemberData {
  effectiveRights: TeamEffectiveRightsByProductType | null;
}

export interface TeamEffectiveMembersView {
  members: TeamEffectiveMemberData[];
  team: Omit<TeamData, 'createdByUserId' | 'createdAt' | 'updatedAt'>;
}
